var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { atom, DefaultValue, selector, selectorFamily, useRecoilState, useSetRecoilState, } from 'recoil';
import { recoilPersist } from 'recoil-persist';
import { initialSteps } from '../Progressbar';
var defaultValues = {
    shopConfig: {
        shopUri: '',
        trackingPrefix: 'shop',
    },
    product: {},
    customer: {},
    payment: {},
    shipment: {},
    terms: {
        privacyAccepted: false,
        agbAccepted: false,
    },
    progress: {
        switching: false,
        currentStep: 0,
        steps: [],
        orderCompleted: false,
    },
    countries: [],
};
var persistAtom = recoilPersist().persistAtom;
var appErrorValue = atom({
    key: 'AppErrorStateValue',
    default: {},
});
export var appErrorState = selector({
    key: 'AppErrorState',
    get: function (_a) {
        var get = _a.get;
        return get(appErrorValue);
    },
    set: function (_a, newValue) {
        var set = _a.set;
        return set(appErrorValue, newValue);
    },
});
export function useSetAppErrors() {
    var _a = useRecoilState(appErrorState), appError = _a[0], setAppError = _a[1];
    return {
        updateErrorMessage: function (message) { return setAppError(__assign(__assign({}, appError), { message: message })); },
        resetErrorMessage: function () { return setAppError({ message: undefined }); },
    };
}
export var appStateValue = atom({
    key: 'AppStateValue',
    default: defaultValues,
    effects_UNSTABLE: [persistAtom],
});
export var resetAppState = selector({
    key: 'ResetAppState',
    get: function () { return null; },
    set: function (_a) {
        var reset = _a.reset;
        reset(appStateValue);
        reset(appErrorValue);
    },
});
export var appState = selectorFamily({
    key: 'AppState',
    get: function (field) { return function (_a) {
        var get = _a.get;
        if (field && Object.hasOwnProperty.call(get(appStateValue), field)) {
            return get(appStateValue)[field];
        }
        return {};
    }; },
    set: function (field) { return function (_a, newValue) {
        var set = _a.set;
        if (!(newValue instanceof DefaultValue)) {
            return set(appStateValue, function (prevState) {
                var _a;
                return (__assign(__assign({}, prevState), (_a = {}, _a[field] = newValue, _a)));
            });
        }
        return set(appStateValue, newValue);
    }; },
});
export var appOrderState = selector({
    key: 'OrderState',
    get: function (_a) {
        var get = _a.get;
        return get(appState('order'));
    },
    set: function (_a, newValue) {
        var set = _a.set;
        return set(appState('order'), newValue);
    },
});
export var appCustomerState = selector({
    key: 'CustomerState',
    get: function (_a) {
        var get = _a.get;
        return get(appState('customer'));
    },
    set: function (_a, newValue) {
        var set = _a.set;
        return set(appState('customer'), newValue);
    },
});
export var appPaymentState = selector({
    key: 'PaymentState',
    get: function (_a) {
        var get = _a.get;
        return get(appState('payment'));
    },
    set: function (_a, newValue) {
        var set = _a.set;
        return set(appState('payment'), newValue);
    },
});
export var appProductState = selector({
    key: 'ProductState',
    get: function (_a) {
        var _b;
        var get = _a.get;
        return (_b = get(appState('product'))) !== null && _b !== void 0 ? _b : {};
    },
    set: function (_a, newValue) {
        var set = _a.set;
        return set(appState('product'), newValue);
    },
});
export var appProgressState = selector({
    key: 'ProgressState',
    get: function (_a) {
        var get = _a.get;
        return get(appState('progress'));
    },
    set: function (_a, newValue) {
        var set = _a.set;
        return set(appState('progress'), newValue);
    },
});
export var appConfigState = selector({
    key: 'ConfigState',
    get: function (_a) {
        var get = _a.get;
        return get(appState('shopConfig'));
    },
    set: function (_a, newValue) {
        var set = _a.set;
        return set(appState('shopConfig'), newValue);
    },
});
export var appCountriesState = selector({
    key: 'CountriesState',
    get: function (_a) {
        var get = _a.get;
        return get(appState('countries'));
    },
    set: function (_a, newValue) {
        var set = _a.set;
        return set(appState('countries'), newValue);
    },
});
export var appTermsState = selector({
    key: 'TermsState',
    get: function (_a) {
        var get = _a.get;
        return get(appState('terms'));
    },
    set: function (_a, newValue) {
        var set = _a.set;
        return set(appState('terms'), newValue);
    },
});
export function useProgress() {
    var _a = useRecoilState(appProgressState), progress = _a[0], setProgress = _a[1];
    return {
        stepForward: function () {
            setProgress(function (currentProgress) {
                if (currentProgress.currentStep < (currentProgress.steps.length - 1)) {
                    var currentSteps = __spreadArray([], currentProgress.steps, true);
                    currentSteps[currentProgress.currentStep] = __assign(__assign({}, currentSteps[currentProgress.currentStep]), { completed: true });
                    return __assign(__assign({}, currentProgress), { steps: currentSteps, switching: true, currentStep: currentProgress.currentStep + 1 });
                }
                return currentProgress;
            });
        },
        setSteps: function (steps, reset) {
            if (reset === void 0) { reset = false; }
            if (reset) {
                setProgress({
                    steps: initialSteps,
                    switching: false,
                    currentStep: 0,
                    orderCompleted: false,
                });
                return;
            }
            setProgress(function (currentProgress) { return (__assign(__assign({}, currentProgress), { steps: steps })); });
        },
        stepBackward: function () {
            setProgress(function (currentProgress) {
                if (currentProgress.currentStep > 0) {
                    return __assign(__assign({}, currentProgress), { switching: true, currentStep: currentProgress.currentStep - 1 });
                }
                return currentProgress;
            });
        },
        setSwitching: function (isSwitching) {
            setProgress(function (currentProgress) { return (__assign(__assign({}, currentProgress), { switching: isSwitching })); });
        },
        getCurrentRoute: function () { var _a, _b; return (_b = (_a = progress.steps[progress.currentStep]) === null || _a === void 0 ? void 0 : _a.route) !== null && _b !== void 0 ? _b : '/'; },
        removeStep: function (route) {
            setProgress(function (currentProgress) {
                var steps = __spreadArray([], currentProgress.steps, true);
                var stepToRemove = steps.find(function (step) { return step.route === route; });
                if (stepToRemove) {
                    var index = steps.indexOf(stepToRemove);
                    if (index >= 0) {
                        steps.splice(index, 1);
                        return __assign(__assign({}, currentProgress), { steps: steps });
                    }
                }
                return currentProgress;
            });
        },
        addStep: function (newStep, position) {
            setProgress(function (currentProgress) {
                var stepToAdd = __assign({}, newStep);
                var existingSteps = __spreadArray([], currentProgress.steps, true);
                var existingStep = existingSteps.find(function (step) { return step.route === stepToAdd.route; });
                // To ensure a consistent state, we always remove potentially existing steps
                // with the same route
                if (existingStep) {
                    stepToAdd.completed = existingStep.completed;
                    existingSteps.splice(existingSteps.indexOf(existingStep), 1);
                }
                existingSteps.splice(position, 0, stepToAdd);
                return __assign(__assign({}, currentProgress), { steps: existingSteps });
            });
        },
        completeOrder: function () {
            setProgress(function (currentProgress) { return (__assign(__assign({}, currentProgress), { orderCompleted: true })); });
        },
    };
}
export function useTerms() {
    var setTerms = useSetRecoilState(appTermsState);
    return {
        toggleAgbAccepted: function () { return setTerms(function (currentTerms) { return (__assign(__assign({}, currentTerms), { agbAccepted: !currentTerms.agbAccepted })); }); },
        togglePrivacyAccepted: function () { return setTerms(function (currentTerms) { return (__assign(__assign({}, currentTerms), { privacyAccepted: !currentTerms.privacyAccepted })); }); },
    };
}
